import { Drawer, Tooltip } from "antd";

import ToggleSwitch from "../ToggleSwitch/ToggleSwitch";
import { Button } from "../index";
import { AppStrings } from "../../../utils/constants/AppStrings";
import styles from "./AsyncExportDrawer.module.css";
import type { AsyncExportLabels, ExportColumnOption } from "./types";

export type AsyncExportDrawerProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly columns: readonly ExportColumnOption[];
  readonly selectedKeys: ReadonlySet<string>;
  readonly onToggleColumn: (key: string, checked: boolean) => void;
  readonly onToggleAll: (checked: boolean) => void;
  readonly onSubmit: () => void;
  readonly submitting: boolean;
  /** Optional string overrides — all fields fall back to AppStrings.EXPORT_*. */
  readonly labels?: AsyncExportLabels;
};

/**
 * Column-picker drawer for the generic async export flow.
 *
 * Purely presentational — state (selection set, drawer open flag,
 * in-flight job status) lives on the parent `AsyncExportButton`. That
 * split keeps the drawer trivial to snapshot in tests and lets the parent
 * decide how to source the vocabulary (props today, could be a hook
 * tomorrow) without touching the picker.
 */
function AsyncExportDrawer({
  open,
  onClose,
  columns,
  selectedKeys,
  onToggleColumn,
  onToggleAll,
  onSubmit,
  submitting,
  labels,
}: AsyncExportDrawerProps) {
  const total = columns.length;
  const selectedCount = selectedKeys.size;
  const allSelected = total > 0 && selectedCount === total;
  const canSubmit = selectedCount > 0;

  const title = labels?.drawerTitle ?? AppStrings.EXPORT_DRAWER_TITLE;
  const selectAllLabel =
    labels?.selectAllLabel ?? AppStrings.EXPORT_DRAWER_SELECT_ALL;
  const confirmLabel =
    labels?.confirmLabel ?? AppStrings.EXPORT_DRAWER_CONFIRM;
  const emptyTooltip =
    labels?.emptySelectionTooltip ??
    AppStrings.EXPORT_DRAWER_EMPTY_TOOLTIP;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={title}
      placement="right"
      width={400}
      destroyOnClose
      data-testid="async-export-drawer"
      classNames={{ body: styles.drawerBody }}
    >
      <div className={styles.selectAllRow}>
        <ToggleSwitch
          value={allSelected}
          onChange={onToggleAll}
          sliderClassName={styles.selectAllSlider}
        />
        <span className={styles.selectAllLabel}>{selectAllLabel}</span>
      </div>

      <hr className={styles.divider} />

      <ul className={styles.columnList} data-testid="async-export-column-list">
        {columns.map((col) => {
          // Locked columns render disabled + always-checked. The container
          // enforces the same invariant on `selectedKeys` so the visual and
          // the payload stay in sync even if a caller forgets to pre-seed
          // the selection with locked keys.
          const locked = Boolean(col.locked);
          const checked = locked || selectedKeys.has(col.key);
          const testId = locked
            ? `async-export-column-${col.key}-locked`
            : `async-export-column-${col.key}`;
          return (
            <li
              key={col.key}
              className={styles.columnRow}
              data-testid={testId}
            >
              <ToggleSwitch
                value={checked}
                disabled={locked}
                ariaLabel={col.label}
                onChange={(next) => {
                  if (locked) return;
                  onToggleColumn(col.key, next);
                }}
              />
              <span className={styles.columnLabel}>{col.label}</span>
            </li>
          );
        })}
      </ul>

      <div className={styles.footer}>
        <Tooltip
          title={canSubmit ? "" : emptyTooltip}
          placement="topRight"
        >
          <span className={styles.confirmWrapper}>
            <Button
              intent="primary"
              size="large"
              onClick={onSubmit}
              disabled={!canSubmit || submitting}
              loading={submitting}
              data-testid="async-export-drawer-confirm"
            >
              {confirmLabel}
            </Button>
          </span>
        </Tooltip>
      </div>
    </Drawer>
  );
}

export default AsyncExportDrawer;
